import React, { useEffect, useMemo, useState } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { EnumInput } from "@/components/ui/EnumInput";
import { DateInput } from "@/components/ui/DateInput";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { NumericInput } from "@/components/ui/NumericInput";
import { TenantAddressType } from "@/types/db/tenants";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { InvoiceForm } from "./types";
import { INVOICE_TERMS, LineItem } from "@/types/db/invoices";
import { groupLineItems } from "../utils/groupLineItems";
import { combineLineItems } from "../utils/combineLineItems";
import useDateTime from "@/hooks/useDateTime";
import { projectService } from "@/services/projectService";

/** Round to nearest cent. */
const round2 = (n: number) => Math.round(n * 100) / 100;

function calculateDueDate(invoiceDate?: Date | string, terms?: string): string {
  if (!invoiceDate) return "";
  const days = terms ? parseInt(terms.replace(/\D/g, ""), 10) : 0;
  if (!days) return "";
  const due = new Date(invoiceDate);
  due.setDate(due.getDate() + days);
  return due.toLocaleDateString();
}

interface InvoiceFieldsProps {
  invoice: InvoiceForm;
  fieldErrors?: { client_id?: string; project_id?: string };
  selectedAddress: TenantAddressType | null;
  addresses: TenantAddressType[];
  updateInvoice: (patch: Partial<InvoiceForm>) => void;
  onAddressSelect: (address: TenantAddressType) => void;
  adjustmentAmount?: number;
  onAdjustmentChange?: (amount: number) => void;
}

export function InvoiceFields({
  invoice,
  fieldErrors,
  selectedAddress,
  addresses,
  updateInvoice,
  onAddressSelect,
  adjustmentAmount = 0,
  onAdjustmentChange,
}: InvoiceFieldsProps) {
  const { colors } = useTheme();
  const { formatTime } = useDateTime();
  const combineAdminFee = invoice.meta_data?.combine_admin_fee ?? true;
  const combineDiscount = invoice.meta_data?.combine_discount ?? true;

  const toggleCombineAdminFee = () => {
    updateInvoice({
      meta_data: {
        ...invoice.meta_data,
        status: invoice.meta_data?.status ?? "DRAFT",
        line_items: invoice.meta_data?.line_items ?? [],
        combine_admin_fee: !combineAdminFee,
      },
    });
  };

  const toggleCombineDiscount = () => {
    updateInvoice({
      meta_data: {
        ...invoice.meta_data,
        status: invoice.meta_data?.status ?? "DRAFT",
        line_items: invoice.meta_data?.line_items ?? [],
        combine_discount: !combineDiscount,
      },
    });
  };

  const fmtTime = (iso?: string) => (iso ? formatTime(iso) : "—");

  // Local adjustments list (UI-only for now)
  const [adjustments, setAdjustments] = useState<{ id: number; description: string; amount: number }[]>([]);
  const [nextAdjId, setNextAdjId] = useState(1);

  const addAdjustment = () => {
    setAdjustments((prev) => [...prev, { id: nextAdjId, description: "", amount: 0 }]);
    setNextAdjId((n) => n + 1);
  };

  const updateAdjustment = (id: number, patch: { description?: string; amount?: number }) => {
    setAdjustments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };

  const removeAdjustment = (id: number) => {
    setAdjustments((prev) => prev.filter((a) => a.id !== id));
  };

  const [projectDeposit, setProjectDeposit] = useState<number | null>(null);
  const depositApplied = invoice.meta_data?.deposit_applied ?? 0;

  useEffect(() => {
    if (!invoice.project_id) {
      setProjectDeposit(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const project = await projectService.getProjectById(invoice.project_id);
        if (!cancelled) {
          const amt = project?.meta_data?.deposit_amount;
          setProjectDeposit(typeof amt === "number" && !isNaN(amt) ? amt : null);
        }
      } catch {
        if (!cancelled) setProjectDeposit(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoice.project_id]);

  const handleDepositChange = (value: number) => {
    const clamped = Math.min(Math.max(value, 0), projectDeposit ?? 0);
    updateInvoice({
      meta_data: {
        ...invoice.meta_data,
        status: invoice.meta_data?.status ?? "DRAFT",
        line_items: invoice.meta_data?.line_items ?? [],
        deposit_applied: clamped || undefined,
      },
    });
  };

  const { displayItems, combinedAdminFeeTotal, combinedDiscountTotal } = useMemo(() => {
    const raw = invoice.meta_data?.line_items ?? [];
    const { items: combined, adminFeeTotal, discountTotal } = combineLineItems(raw, combineAdminFee, combineDiscount);
    let items = combined;
    // Append legacy single adjustment if present
    if (adjustmentAmount !== 0) {
      items = [
        ...items,
        {
          description: "Adjustment",
          unit_price: adjustmentAmount,
          quantity: 1,
          amount: adjustmentAmount,
        },
      ];
    }
    // Append new multi-adjustments
    if (adjustments.length > 0) {
      const adjItems = adjustments
        .filter((a) => a.amount !== 0)
        .map((a) => ({
          description: a.description || "Adjustment",
          unit_price: a.amount,
          quantity: 1,
          amount: a.amount,
        }));
      items = [...items, ...adjItems];
    }
    return { displayItems: items, combinedAdminFeeTotal: adminFeeTotal, combinedDiscountTotal: discountTotal };
  }, [invoice.meta_data?.line_items, adjustmentAmount, combineAdminFee, combineDiscount, adjustments]);

  return (
    <View style={{ gap: 16 }}>
      <View style={{ gap: 16, flexDirection: "row", flexWrap: "wrap" }}>
        <DateInput
          label="Invoice Date"
          mode="date"
          value={invoice.meta_data?.invoice_date || new Date()}
          style={{ flexBasis: "30%", flexGrow: 1 }}
          onChange={(date) =>
            updateInvoice({
              meta_data: {
                ...invoice.meta_data,
                status: invoice.meta_data?.status ?? "DRAFT",
                line_items: invoice.meta_data?.line_items ?? [],
                invoice_date: date,
              },
            })
          }
        />
        <ThemedInput
          testID="invoice-po-number"
          label="PO Number"
          style={{ flexBasis: "30%", flexGrow: 1 }}
          placeholder="Optional"
          value={invoice.purchar_order_number || ""}
          onChangeText={(v) => updateInvoice({ purchar_order_number: v })}
        />
        <EnumInput
          label="Terms"
          options={[...INVOICE_TERMS]}
          value={invoice.meta_data?.terms}
          placeholder="Select terms..."
          style={{ flexBasis: "30%", flexGrow: 1 }}
          onSelect={(value) =>
            updateInvoice({
              meta_data: {
                ...invoice.meta_data,
                status: invoice.meta_data?.status ?? "DRAFT",
                line_items: invoice.meta_data?.line_items ?? [],
                terms: value,
              },
            })
          }
        />
        {invoice.meta_data?.invoice_date && invoice.meta_data?.terms && (
          <ThemedInput
            label="Due Date (calculated)"
            editable={false}
            value={calculateDueDate(invoice.meta_data.invoice_date, invoice.meta_data.terms)}
            style={{ flexBasis: "30%", flexGrow: 1 }}
          />
        )}
      </View>

      <ClientInput
        value={invoice.client_id || null}
        state="editing"
        label="Client *"
        error={fieldErrors?.client_id}
        onChange={(clientId) => updateInvoice({ client_id: clientId || "", project_id: "" })}
      />

      <ProjectInput
        value={invoice.project_id || null}
        clientId={invoice.client_id || null}
        state="editing"
        showDeposit
        label="Project *"
        error={fieldErrors?.project_id}
        onChange={(projectId) => updateInvoice({ project_id: projectId || "" })}
      />

      {projectDeposit != null && projectDeposit > 0 && (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ flex: 1 }}>
            <NumericInput value={depositApplied} onChangeValue={handleDepositChange} placeholder="0.00" />
          </View>
          <ThemedText style={{ fontSize: 12, color: colors.textSecondary }}>
            of {`$${projectDeposit.toFixed(2)}`} deposit
          </ThemedText>
        </View>
      )}

      {/* Remittance Address Selection */}
      <EnumInput
        label="Remittance Address"
        options={addresses.map((a) => a.name)}
        value={selectedAddress?.name || ""}
        placeholder="Select address..."
        onSelect={(name) => {
          const address = addresses.find((a) => a.name === name);
          if (address) onAddressSelect(address);
        }}
      />

      {/* Line Items Section */}
      {displayItems.length > 0 && (
        <View style={{ gap: 8 }}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
            <ThemedText style={{ fontSize: 14, fontWeight: "600" }}>Line Items ({displayItems.length})</ThemedText>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
              <Pressable style={{ flexDirection: "row", alignItems: "center", gap: 6 }} onPress={toggleCombineAdminFee}>
                <MaterialCommunityIcons
                  name={combineAdminFee ? "checkbox-marked" : "checkbox-blank-outline"}
                  size={20}
                  color={combineAdminFee ? colors.primary : colors.textSecondary}
                />
                <ThemedText style={{ fontSize: 13, color: colors.textSecondary }}>Combine Admin Fee</ThemedText>
              </Pressable>
              <Pressable style={{ flexDirection: "row", alignItems: "center", gap: 6 }} onPress={toggleCombineDiscount}>
                <MaterialCommunityIcons
                  name={combineDiscount ? "checkbox-marked" : "checkbox-blank-outline"}
                  size={20}
                  color={combineDiscount ? colors.primary : colors.textSecondary}
                />
                <ThemedText style={{ fontSize: 13, color: colors.textSecondary }}>Combine Discount</ThemedText>
              </Pressable>
              <Pressable style={{ flexDirection: "row", alignItems: "center", gap: 4 }} onPress={addAdjustment}>
                <MaterialCommunityIcons name="plus" size={20} color={colors.primary} />
                <ThemedText style={{ fontSize: 13, color: colors.primary }}>Adjustment</ThemedText>
              </Pressable>
            </View>
          </View>

          {/* Adjustment Inputs */}
          {adjustments.length > 0 && (
            <View style={{ gap: 8 }}>
              {adjustments.map((adj) => (
                <View
                  key={adj.id}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                    backgroundColor: colors.backgroundSecondary,
                    borderRadius: 8,
                    borderWidth: StyleSheet.hairlineWidth,
                    borderColor: colors.border,
                    padding: 8,
                  }}
                >
                  <ThemedInput
                    placeholder="Description"
                    value={adj.description}
                    onChangeText={(v) => updateAdjustment(adj.id, { description: v })}
                    style={{ flex: 2 }}
                  />
                  <NumericInput
                    value={adj.amount}
                    onChangeValue={(v) => updateAdjustment(adj.id, { amount: v })}
                    style={{ flex: 1 }}
                  />
                  <Pressable
                    onPress={() => removeAdjustment(adj.id)}
                    style={{
                      padding: 6,
                      borderRadius: 6,
                      backgroundColor: colors.background,
                    }}
                  >
                    <MaterialCommunityIcons name="undo" size={18} color={colors.textSecondary} />
                  </Pressable>
                </View>
              ))}
            </View>
          )}
          <View
            style={{
              borderRadius: 8,
              borderWidth: StyleSheet.hairlineWidth,
              borderColor: colors.border,
              backgroundColor: colors.backgroundSecondary,
              overflow: "hidden",
            }}
          >
            {/* Header Row */}
            <View
              style={{
                flexDirection: "row",
                padding: 12,
                borderBottomWidth: StyleSheet.hairlineWidth,
                borderBottomColor: colors.border,
                backgroundColor: colors.background,
              }}
            >
              <ThemedText style={{ flex: 2, fontWeight: "600", fontSize: 12 }}>Description</ThemedText>
              <ThemedText style={{ flex: 1, fontWeight: "600", fontSize: 12, textAlign: "right" }}>Time In</ThemedText>
              <ThemedText style={{ flex: 1, fontWeight: "600", fontSize: 12, textAlign: "right" }}>Time Out</ThemedText>
              <ThemedText style={{ flex: 1, fontWeight: "600", fontSize: 12, textAlign: "right" }}>Qty</ThemedText>
              <ThemedText style={{ flex: 1, fontWeight: "600", fontSize: 12, textAlign: "right" }}>Rate</ThemedText>
              {!combineAdminFee && (
                <ThemedText style={{ flex: 1, fontWeight: "600", fontSize: 12, textAlign: "right" }}>
                  Admin Fee
                </ThemedText>
              )}
              {!combineDiscount && (
                <ThemedText style={{ flex: 1, fontWeight: "600", fontSize: 12, textAlign: "right" }}>
                  Discount
                </ThemedText>
              )}
              <ThemedText style={{ flex: 1, fontWeight: "600", fontSize: 12, textAlign: "right" }}>Amount</ThemedText>
            </View>

            {/* Grouped Line Item Rows */}
            {(() => {
              const groups = groupLineItems(displayItems);
              const hasGroups = groups.length > 1 || (groups.length === 1 && groups[0].shiftName !== "Other");

              if (!hasGroups) {
                // Flat rendering for ungrouped items
                return displayItems.map((item: LineItem, index: number) => (
                  <View
                    key={index}
                    style={{
                      flexDirection: "row",
                      padding: 12,
                      borderBottomWidth: index < displayItems.length - 1 ? StyleSheet.hairlineWidth : 0,
                      borderBottomColor: colors.border,
                    }}
                  >
                    <ThemedText style={{ flex: 2, fontSize: 13 }} numberOfLines={2}>
                      {item.description}
                    </ThemedText>
                    <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                      {fmtTime(item.time_in)}
                    </ThemedText>
                    <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                      {fmtTime(item.time_out)}
                    </ThemedText>
                    <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                      {item.quantity.toFixed(2)}
                    </ThemedText>
                    <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                      ${item.unit_price.toFixed(2)}
                    </ThemedText>
                    {!combineAdminFee && (
                      <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                        {item.admin_fee ? `${item.admin_fee}%` : "—"}
                      </ThemedText>
                    )}
                    {!combineDiscount && (
                      <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                        {item.discount ? `${item.discount}%` : "—"}
                      </ThemedText>
                    )}
                    <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right", fontWeight: "500" }}>
                      ${item.amount.toFixed(2)}
                    </ThemedText>
                  </View>
                ));
              }

              // Grouped rendering
              return groups.map((shiftGroup, gi) => (
                <View key={`shift-${gi}`}>
                  {/* Shift Header */}
                  <View
                    style={{
                      flexDirection: "row",
                      padding: 12,
                      paddingVertical: 8,
                      backgroundColor: colors.background,
                      borderBottomWidth: StyleSheet.hairlineWidth,
                      borderBottomColor: colors.border,
                      borderLeftWidth: 3,
                      borderLeftColor: colors.primary,
                    }}
                  >
                    <ThemedText style={{ flex: 8, fontWeight: "700", fontSize: 12 }}>{shiftGroup.shiftName}</ThemedText>
                    <ThemedText style={{ flex: 1, fontWeight: "600", fontSize: 12, textAlign: "right" }}>
                      ${shiftGroup.subtotal.toFixed(2)}
                    </ThemedText>
                  </View>

                  {shiftGroup.positions.map((posGroup, pi) => (
                    <View key={`pos-${gi}-${pi}`}>
                      {/* Position Sub-header (only show if more than one position or not "General") */}
                      {(shiftGroup.positions.length > 1 || posGroup.positionName !== "General") && (
                        <View
                          style={{
                            flexDirection: "row",
                            paddingHorizontal: 12,
                            paddingVertical: 6,
                            borderBottomWidth: StyleSheet.hairlineWidth,
                            borderBottomColor: colors.border,
                            paddingLeft: 20,
                          }}
                        >
                          <ThemedText style={{ flex: 8, fontSize: 11, fontStyle: "italic", opacity: 0.7 }}>
                            {posGroup.positionName}
                          </ThemedText>
                          <ThemedText style={{ flex: 1, fontSize: 11, textAlign: "right", opacity: 0.7 }}>
                            ${posGroup.subtotal.toFixed(2)}
                          </ThemedText>
                        </View>
                      )}

                      {/* Line Items */}
                      {posGroup.items.map((item, ii) => (
                        <View
                          key={`item-${gi}-${pi}-${ii}`}
                          style={{
                            flexDirection: "row",
                            padding: 12,
                            paddingLeft: 20,
                            borderBottomWidth: StyleSheet.hairlineWidth,
                            borderBottomColor: colors.border,
                          }}
                        >
                          <ThemedText style={{ flex: 2, fontSize: 13 }} numberOfLines={2}>
                            {item.description}
                          </ThemedText>
                          <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                            {fmtTime(item.time_in)}
                          </ThemedText>
                          <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                            {fmtTime(item.time_out)}
                          </ThemedText>
                          <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                            {item.quantity.toFixed(2)}
                          </ThemedText>
                          <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                            ${item.unit_price.toFixed(2)}
                          </ThemedText>
                          {!combineAdminFee && (
                            <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                              {item.admin_fee ? `${item.admin_fee}%` : "—"}
                            </ThemedText>
                          )}
                          {!combineDiscount && (
                            <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right" }}>
                              {item.discount ? `${item.discount}%` : "—"}
                            </ThemedText>
                          )}
                          <ThemedText style={{ flex: 1, fontSize: 13, textAlign: "right", fontWeight: "500" }}>
                            ${item.amount.toFixed(2)}
                          </ThemedText>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              ));
            })()}

            {/* Totals footer: Subtotal (pre-adjustment base) → Admin Fee (+) /
                Discount (−) when combined → Total. */}
            {(() => {
              const subtotal = round2(
                displayItems.reduce((sum: number, item: LineItem) => sum + (item.amount || 0), 0),
              );
              const total = round2(subtotal + combinedAdminFeeTotal - combinedDiscountTotal);

              const rows: { label: string; value: number; grand?: boolean }[] = [
                { label: "Subtotal", value: subtotal },
              ];
              if (combineAdminFee && combinedAdminFeeTotal > 0) {
                rows.push({ label: "Admin Fee", value: combinedAdminFeeTotal });
              }
              if (combineDiscount && combinedDiscountTotal > 0) {
                rows.push({ label: "Discount", value: -combinedDiscountTotal });
              }
              rows.push({ label: "Total", value: total, grand: true });

              return (
                <View
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderTopWidth: StyleSheet.hairlineWidth,
                    borderTopColor: colors.border,
                    backgroundColor: colors.background,
                  }}
                >
                  {rows.map((r) => (
                    <View key={r.label} style={{ flexDirection: "row", alignItems: "center", paddingVertical: 3 }}>
                      <ThemedText
                        style={{
                          flex: 1,
                          textAlign: "right",
                          fontSize: 13,
                          fontWeight: r.grand ? "600" : "400",
                          opacity: r.grand ? 1 : 0.7,
                        }}
                      >
                        {r.label}
                      </ThemedText>
                      <ThemedText
                        style={{
                          width: 96,
                          textAlign: "right",
                          fontSize: 13,
                          fontWeight: r.grand ? "600" : "400",
                        }}
                      >
                        ${r.value.toFixed(2)}
                      </ThemedText>
                    </View>
                  ))}
                </View>
              );
            })()}
          </View>
        </View>
      )}
    </View>
  );
}
